/**
 * Header Component
 * 
 * Main site header with locked Option C three-line wordmark (navy + red brand lock)
 * Breadcrumbs are always visible and sticky when scrolling
 * 
 * Wordmark colors (on navy):
 * - COLLEGIATE: soft grey #A8B0BC
 * - NFL: red #E10600
 * - IMPACT: white #FFFFFF
 */

import React from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Breadcrumbs, getRankingsBreadcrumbs, getCollegeBreadcrumbs, getCollegeCareerBreadcrumbs, getConferenceCareerBreadcrumbs, getPlayersBreadcrumbs, getPlayersCareerBreadcrumbs, getAboutBreadcrumbs, getClassesBreadcrumbs } from "../navigation/Breadcrumbs";
import { parseRankingsUrl, parseCollegeUrl, parsePlayersUrl } from "../../utils/urlUtils";
import { parseClassPath } from "../../utils/classBreakdown";
import { parseCollegeCareerPath } from "../../utils/collegeCareer";
import { parseConferenceCareerPath } from "../../utils/conferenceCareer";
import { parsePlayerCareerPath } from "../../utils/playerCareer";
import { getConferenceNameFromAbbreviation } from "../../utils/conferenceUtils";
import { OTHER_CONFERENCE_ID } from "../../utils/collegeMeta";
import { collegeNameForSlug } from "../../utils/collegeMeta";
import collegeDirectory from "../../data/collegeData.json";
import { createSlugToKeyMap } from "../../utils/collegeUtils";
import { CURRENT_YEAR } from "../../constants/app";
import { useCollegiateData } from "../../hooks/useCollegiateData";
import worker from "workerize-loader!../../workers/data/core.js";

const instanceWorker = worker();

/**
 * Locked Option C Three-Line Wordmark Component
 * Inter Tight / bold condensed; NFL line clearly largest
 */
const CNIWordmark: React.FC = () => (
  <div className="cni-wordmark" aria-label="Collegiate NFL Impact">
    <span className="cni-wordmark-collegiate">COLLEGIATE</span>
    <span className="cni-wordmark-nfl">NFL</span>
    <span className="cni-wordmark-impact">IMPACT</span>
  </div>
);

/**
 * X/Twitter Icon Component
 * Simple X logo for social link
 */
const XIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const Header: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pathname = location.pathname;

  const careerPath = parseCollegeCareerPath(pathname);
  const isCollegeSeasonPage = pathname.startsWith("/college/") && !careerPath;
  const { collegiateFantasyData } = useCollegiateData({
    year: CURRENT_YEAR,
    statFilter: '',
    conferenceView: false,
    worker: isCollegeSeasonPage ? instanceWorker : undefined,
  });

  // Determine active nav item
  const getActiveNav = (): 'rankings' | 'players' | 'classes' | 'conferences' | 'about' | null => {
    if (pathname === '/about') return 'about';
    if (pathname.startsWith('/classes')) return 'classes';
    if (pathname.startsWith('/conferences')) return 'conferences';
    if (pathname.startsWith('/players')) return 'players';
    if (pathname.startsWith('/rankings')) {
      if (pathname.includes('/conference')) return 'conferences';
      return 'rankings';
    }
    return null;
  };
  
  const activeNav = getActiveNav();

  const getBreadcrumbItems = () => {
    // Homepage: no breadcrumb trail (lone "Home" adds noise under the hero)
    if (pathname === "/") {
      return [];
    }

    if (pathname === "/about") {
      return getAboutBreadcrumbs();
    }

    if (pathname.startsWith("/classes")) {
      const parsed = parseClassPath(pathname);
      let collegeName: string | null = null;
      if (parsed?.collegeSlug) {
        const colleges = (collegeDirectory as { colleges: Record<string, { slug?: string; displayName?: string }> }).colleges;
        collegeName =
          Object.values(colleges).find((college) => college.slug === parsed.collegeSlug)?.displayName ||
          parsed.collegeSlug;
      }
      return getClassesBreadcrumbs(parsed?.classYear, parsed?.view, collegeName, parsed?.collegeSlug);
    }

    const playerCareer = parsePlayerCareerPath(pathname);
    if (playerCareer) {
      return getPlayersCareerBreadcrumbs(playerCareer.stat);
    }

    if (pathname.startsWith("/players")) {
      const parsed = parsePlayersUrl(pathname);
      return getPlayersBreadcrumbs(
        parsed.statCategory || undefined,
        parsed.year,
        parsed.week,
        parsed.yearAggregate,
        parsed.view
      );
    }

    const conferenceCareer = parseConferenceCareerPath(pathname);
    if (conferenceCareer) {
      const conferenceName =
        conferenceCareer.abbr === OTHER_CONFERENCE_ID
          ? "Independent / other"
          : conferenceCareer.abbr
            ? getConferenceNameFromAbbreviation(conferenceCareer.abbr)
            : null;
      return getConferenceCareerBreadcrumbs(
        conferenceName,
        conferenceCareer.abbr,
        conferenceCareer.stat
      );
    }

    if (careerPath) {
      const collegeName = collegeNameForSlug(careerPath.slug) || careerPath.slug;
      return getCollegeCareerBreadcrumbs(collegeName, careerPath.slug, careerPath.stat);
    }

    if (pathname.startsWith("/college/")) {
      const parsed = parseCollegeUrl(pathname);
      if (parsed) {
        let collegeName = parsed.slug || "Unknown";
        if (collegiateFantasyData?.colleges) {
          const slugToKeyMap = createSlugToKeyMap(collegiateFantasyData.colleges);
          const collegeKey = slugToKeyMap.get(parsed.slug);
          if (collegeKey && collegiateFantasyData.colleges[collegeKey]) {
            collegeName = collegiateFantasyData.colleges[collegeKey].displayName || collegeName;
          }
        }

        const conferenceView = searchParams.get('conferenceView') === 'true';
        const conferenceAbbr = searchParams.get('conferenceAbbr') || null;

        const isCollegeHubPath = /^\/college\/[^/]+$/.test(pathname);
        const breadcrumbYear = parsed.year ?? (isCollegeHubPath ? CURRENT_YEAR : undefined);

        return getCollegeBreadcrumbs(
          collegeName,
          parsed.slug,
          parsed.statFilter || '',
          breadcrumbYear,
          parsed.week,
          parsed.yearAggregate,
          conferenceView,
          conferenceAbbr
        );
      }
    }

    const parsed = parseRankingsUrl(pathname);
    return getRankingsBreadcrumbs(
      parsed.statCategory || undefined,
      parsed.year,
      parsed.week,
      parsed.yearAggregate,
      parsed.conferenceView,
      parsed.conferenceAbbr
    );
  };

  const breadcrumbItems = getBreadcrumbItems();

  return (
    <header className="site-header">
      {/* Top section: Logo and Navigation */}
      <div className="site-header-main">
        {/* Logo - Left side */}
        <Link to="/" className="site-header-logo-link">
          <CNIWordmark />
        </Link>
        <nav className="site-header-nav" aria-label="Main navigation">
          <ul className="site-header-nav-list">
            <li>
              <Link 
                to="/rankings"
                className={`site-header-nav-link ${activeNav === 'rankings' ? 'active' : ''}`}
              >
                Rankings
              </Link>
            </li>
            <li>
              <Link
                to="/players"
                className={`site-header-nav-link ${activeNav === 'players' ? 'active' : ''}`}
              >
                Players
              </Link>
            </li>
            <li>
              <Link
                to="/classes"
                className={`site-header-nav-link ${activeNav === 'classes' ? 'active' : ''}`}
              >
                Classes
              </Link>
            </li>
            <li>
              <Link 
                to={`/rankings/${CURRENT_YEAR}/conference`} 
                className={`site-header-nav-link ${activeNav === 'conferences' ? 'active' : ''}`}
              >
                Conferences
              </Link>
            </li>
            <li>
              <a
                href="https://x.com/C_NFL_Impact"
                target="_blank"
                rel="noopener noreferrer"
                className="site-header-social-link"
                aria-label="Follow @C_NFL_Impact on X"
              >
                <XIcon size={16} />
              </a>
            </li>
          </ul>
        </nav>
      </div>

      {/* Breadcrumbs section - always visible */}
      {breadcrumbItems && breadcrumbItems.length > 0 && (
        <div className="site-header-breadcrumbs">
          <Breadcrumbs 
            items={breadcrumbItems} 
            preserveSearchParams={true}
          />
        </div>
      )}
    </header>
  );
};
